import {
  BatteryCharging,
  Calendar,
  Car,
  Clock,
  Cpu,
  CreditCard,
  Globe,
  MapPin,
  PieChart,
  Plug,
  Settings,
  Wrench,
  Zap,
} from "lucide-react"
import type { NavItem } from "../types/navigation"
import { customerFleetPath, customerPath } from "./customer-paths"

/**
 * Get navigation configuration for a specific customer.
 *
 * Flat list grouped by `section` (GRIDBOX design): un-sectioned items render
 * first, then the "Assets" and "Operations" groups. "Company settings" is
 * flagged `footer` so the sidebar renders it in its bottom block.
 * @param customerId - The customer UUID to use in navigation paths
 */
export function getNavigationConfig(customerId: string): NavItem[] {
  return [
    { label: "Overview", path: customerPath(customerId, "overview"), icon: Globe },
    { label: "Report", path: customerPath(customerId, "reports"), icon: PieChart },

    {
      label: "Site",
      path: customerFleetPath(customerId, "sites"),
      icon: MapPin,
      section: "Assets",
    },
    {
      // Read-only device cards (Massi, 2026-10-15).
      label: "Devices",
      path: customerFleetPath(customerId, "devices"),
      icon: Cpu,
      section: "Assets",
    },
    {
      label: "BESS",
      path: customerFleetPath(customerId, "batteries"),
      icon: BatteryCharging,
      section: "Assets",
    },
    {
      label: "Maintenance",
      path: customerFleetPath(customerId, "batteries/maintenance"),
      icon: Wrench,
      section: "Assets",
      sub: true,
    },
    {
      label: "Chargers",
      path: customerFleetPath(customerId, "chargers"),
      icon: Plug,
      section: "Assets",
    },
    {
      label: "Charging Schedules",
      path: customerFleetPath(customerId, "charging-requirements"),
      icon: Clock,
      section: "Assets",
      sub: true,
    },
    {
      label: "Vehicles",
      path: customerFleetPath(customerId, "vehicles"),
      icon: Car,
      section: "Assets",
    },
    {
      label: "Trip Schedules",
      path: customerFleetPath(customerId, "trip-schedule"),
      icon: Calendar,
      section: "Assets",
      sub: true,
    },

    {
      label: "RFID Cards",
      path: customerFleetPath(customerId, "rfid-cards"),
      icon: CreditCard,
      section: "Operations",
    },
    {
      label: "Grid Services",
      path: customerPath(customerId, "grid-services"),
      icon: Zap,
      section: "Operations",
      requiresGlobalAdmin: true,
    },

    {
      label: "Company settings",
      path: customerPath(customerId, "company-settings"),
      icon: Settings,
      footer: true,
    },
  ]
}

/**
 * Resolve the human-readable title for the active route, for the top bar.
 * Picks the nav item whose `path` is the longest prefix of `pathname` (so
 * detail routes like `…/assets/sites/:id` still resolve to "Site"); falls
 * back to a title-cased last path segment when nothing matches.
 */
export function getActiveNavTitle(pathname: string, items: NavItem[]): string {
  let best: NavItem | undefined
  for (const item of items) {
    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      if (!best || item.path.length > best.path.length) best = item
    }
  }
  if (best) return best.label

  const segment = pathname.split("/").filter(Boolean).pop() ?? ""
  return segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}
